import React, { useEffect, useRef, useState } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Button, Tag, WysiwygField } from '../../../components';
import type { ParagraphActivity as Type } from '../../../types/uncinc_profile/paragraphs';

import './style.scss';

const ACTIVITY_MAX_HEIGHT = 144; // px.

interface ParagraphActivityProps {
  entity: ImmutableEntity<Type>;
}

const ParagraphActivity: React.FC<ParagraphActivityProps> = ({
  entity,
}) => {
  const { t } = useTranslation();

  const contentRef = useRef<HTMLDivElement>(null);

  const [isExpanded, setExpanded] = useState<boolean | null>(null);

  const activity = entity.toJS();
  const uuid = activity.uuid[0].value;
  const activityType = activity.field_activity_types[0].entity?.name[0].value;
  const dayProgramTypes = activity.field_day_program_types;
  const body = activity.field_body[0]?.processed;
  const groupSize = activity.field_group_size[0]?.value;
  const groups = activity.field_groups[0]?.value;

  const contentInnerClasses = c('Activity__content__inner', {
    'Activity__content__inner--can-expand': isExpanded !== null,
    'Activity__content__inner--expanded': isExpanded === true,
  });

  // Set expanded to false if the activity's height is larger than the max height. This triggers it
  // to be able to expand. Otherwise we'll leave it be.
  useEffect(() => {
    if (!contentRef.current) {
      return;
    }

    if (contentRef.current.getBoundingClientRect().height >= ACTIVITY_MAX_HEIGHT) {
      setExpanded(false);
    }
  }, []);

  return (
    <div id={uuid} className="ParagraphActivity">
      <Container>
        <div className="Activity__inner">
          <div className="Activity__header">
            {activityType && (
              <h3 className="Activity__type">
                {activityType}
              </h3>
            )}

            {(dayProgramTypes && dayProgramTypes.length > 0) && (
              <ul className="Activity__day-program-types">
                {dayProgramTypes.map((type) => {
                  if (!type.entity) {
                    return null;
                  }

                  return (
                    <li key={`day-program-type:${type.target_id}`}>
                      <Tag
                        type="article-type"
                        name={type.entity.name[0].value}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="Activity__content">
            <div ref={contentRef}>
              <div className={contentInnerClasses} id={`activity-content-inner-${uuid}`}>
                {body && (
                  <WysiwygField
                    className="Activity__body"
                    property="schema:text"
                    html={body}
                  />
                )}

                {groupSize && (
                  <>
                    <h4 className="Activity__label">
                      {t('pages.node_project.day_program.group_size')}
                    </h4>
                    <p>{groupSize}</p>
                  </>
                )}

                {groups && (
                  <>
                    <h4 className="Activity__label">
                      {t('pages.node_project.day_program.number_of_groups')}
                    </h4>
                    <p>{groups}</p>
                  </>
                )}
              </div>
            </div>

            {isExpanded !== null && (
              <Button
                className="Activity__content__show-more"
                type="text"
                aria-expanded={isExpanded}
                aria-controls={`activity-content-inner-${uuid}`}
                onClick={() => setExpanded(!isExpanded)}
              >
                {t(`organisms.text.read_${isExpanded ? 'less' : 'more'}`)}
                <span className="u-sr-only">{t('organisms.text.aria_label_extension', { topic: activityType })}</span>
              </Button>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ParagraphActivity;
